import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import infoKnockoutTokens from '@amzn/meridian-tokens/base/icon/info-knockout';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Popover, { PopoverHeader } from '@amzn/meridian/popover';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';
import Tooltip from '@amzn/meridian/tooltip';

import { GridApi } from 'ag-grid-community';
import { Link } from 'react-router-dom';
import { createMssBarSeries, generateMssChartData } from 'src/components/Analytics/utils/mssChartData';
import { MssGridData, MssPivotGridData } from 'src/hooks/analytics';
import GreenDot from 'src/images/greendot.svg';
import { IChartData } from 'src/types/IAnalytics';

import { MetricsLineChart } from '../../MetricsCharts';
import { MetricsGrid } from '../../MetricsGrid';
import {
    MssPivotData,
    getUniqueMonths,
    transformMssPivotGridToPivot,
    transformMssToPivot
} from '../../utils/dataTransformation';
import './MssViewPivot.scss';
import { createMssPivotColDefs, mssPivotColDefsEmpty } from './mssPivotColDefs';

export interface MssViewPivotProps {
    filterText?: string;
    data?: MssPivotGridData[];
    loading?: boolean;
    error?: string;
    isDataFullyLoaded?: boolean;
    completionPercentage?: number;
    serviceId?: number; // Add serviceId prop to determine toggle visibility
    selectedMonths?: string[]; // Array of selected months in YYYYMM format
}

export const MssViewPivot: React.FC<MssViewPivotProps> = ({
    filterText = '',
    data = [],
    loading = false,
    error,
    isDataFullyLoaded = true,
    completionPercentage = 100,
    serviceId = 1, // Default to AMU for backward compatibility
    selectedMonths = []
}) => {
    // Helper function to get loading months from completion percentage
    const getLoadingMonths = (): string[] => {
        if (isDataFullyLoaded || selectedMonths.length === 0) return [];

        const loadedCount = Math.floor((completionPercentage / 100) * selectedMonths.length);
        return selectedMonths.slice(loadedCount);
    };

    const [agGridApi, setAgGridApi] = useState<GridApi>();
    const [filteredTerritories, setFilteredTerritories] = useState<string[]>([]);
    const [filteredVendors, setFilteredVendors] = useState<string[]>([]);

    // Anti-Dilution toggle state (only for AMU serviceId=1)
    const [isAntiDilutionMode, setIsAntiDilutionMode] = useState(true);
    const isAmuService = serviceId === 1;
    const antiDilutionButtonRef = useRef();
    const [antiDilutionMenuOpen, setAntiDilutionMenuOpen] = useState(false);

    // Type guard to check if item is MssPivotGridData
    const isMssPivotGridData = (data: MssPivotGridData | MssGridData): data is MssPivotGridData => {
        return 'eligibleMode' in data && ('actmVendorPlays' in data || 'standardVendorPlays' in data);
    };

    // No need to filter data by Artist Centric anymore - data already contains both ACTM and Standard
    const filteredData = data;

    // Transform filtered data to pivot format
    const pivotData = useMemo(
        () =>
            isAmuService
                ? transformMssPivotGridToPivot(filteredData)
                : transformMssToPivot(filteredData as MssGridData[]),
        [filteredData]
    );

    // Generate chart data (same as regular view)
    const [chartData, setChartData] = useState<IChartData[]>([]);

    // Memoize chart data generation to avoid unnecessary recalculations
    const memoizedChartData = useMemo(() => {
        // Only generate chart data if we have API data
        if (!data || data.length === 0) {
            return [];
        }

        // Convert MssPivotGridData to MssGridData for chart generation when AMU
        let chartDataEntries: MssGridData[] = [];

        // AMU (serviceId = 1): Use eligibleMode to determine which data (ACTM/Standard) to include in charts
        if (isAmuService) {
            filteredData?.forEach((item) => {
                if (isMssPivotGridData(item)) {
                    if (item.eligibleMode && item.eligibleMode !== 'none') {
                        const useActm = item.eligibleMode === 'actm';

                        if (useActm && item.actmVendorPlays != null && item.actmTotalPlays != null) {
                            chartDataEntries.push({
                                month: item.month,
                                territory: item.territory,
                                vendor: item.vendor,
                                vendorPlays: item.actmVendorPlays || 0,
                                totalPlays: item.actmTotalPlays || 0,
                                mss: item.actmMss || 0,
                                musicOnlyVendorPlays: item.actmMusicOnlyVendorPlays,
                                musicOnlyTotalPlays: item.actmMusicOnlyTotalPlays,
                                musicOnlyMss: item.actmMusicOnlyMss,
                                isArtistCentric: true,
                                eligibleForEarningsSubmission: true
                            });
                        } else if (item.eligibleMode === 'standard') {
                            chartDataEntries.push({
                                month: item.month,
                                territory: item.territory,
                                vendor: item.vendor,
                                vendorPlays: item.standardVendorPlays || 0,
                                totalPlays: item.standardTotalPlays || 0,
                                mss: item.standardMss || 0,
                                musicOnlyVendorPlays: item.standardMusicOnlyVendorPlays,
                                musicOnlyTotalPlays: item.standardMusicOnlyTotalPlays,
                                musicOnlyMss: item.standardMusicOnlyMss,
                                isArtistCentric: false,
                                eligibleForEarningsSubmission: true
                            });
                        }
                    }
                }
            });
        } else {
            chartDataEntries = filteredData as MssGridData[];
        }

        // Generate chart data using converted data
        const { barChartData, lineChartData, availableVendors } = generateMssChartData({
            data: chartDataEntries,
            filteredTerritories: filteredTerritories.length > 0 ? filteredTerritories : undefined,
            filteredVendors: filteredVendors.length > 0 ? filteredVendors : undefined,
            isAntiDilutionMode: isAntiDilutionMode
        });

        // Create filter description for titles
        let filterDesc = '';
        if (filteredTerritories.length > 0 || filteredVendors.length > 0) {
            const parts = [];
            if (filteredTerritories.length > 0) {
                parts.push(`Territories: ${filteredTerritories.join(', ')}`);
            }
            if (filteredVendors.length > 0) {
                parts.push(`Vendors: ${filteredVendors.join(', ')}`);
            }
            filterDesc = `Filtered: ${parts.join('; ')}`;
        }

        // Create chart series
        const barSeries = createMssBarSeries(availableVendors);

        return [
            {
                type: 'bar' as const,
                title: 'MSS per Month',
                subtitle: filterDesc,
                data: barChartData,
                xKey: 'month',
                series: barSeries
            },
            {
                type: 'line' as const,
                title: 'MSS Trending by Vendor',
                subtitle: filterDesc,
                data: lineChartData,
                xKey: 'month',
                yKeys: availableVendors
            }
        ];
    }, [filteredData, filteredTerritories, filteredVendors, isAntiDilutionMode]);

    // Get unique months for column definitions
    const availableMonths = useMemo(() => getUniqueMonths(filteredData), [filteredData]);

    // Analyze which column groups have data and should be visible
    const checkColumnVisibility = useCallback((data: MssPivotData[], months: string[]) => {
        const visibility: Record<string, boolean> = {};

        months.forEach((month) => {
            // Check if ACTM group has any non-zero data
            const actmHasData = data.some(
                (row) =>
                    ((row[`${month}_actmVendorPlays`] ?? 0) as number) > 0 ||
                    ((row[`${month}_actmTotalPlays`] ?? 0) as number) > 0 ||
                    ((row[`${month}_actmMss`] ?? 0) as number) > 0
            );

            // Check if Standard group has any non-zero data
            const standardHasData = data.some(
                (row) =>
                    ((row[`${month}_standardVendorPlays`] ?? 0) as number) > 0 ||
                    ((row[`${month}_standardTotalPlays`] ?? 0) as number) > 0 ||
                    ((row[`${month}_standardMss`] ?? 0) as number) > 0
            );

            visibility[`${month}_actm`] = actmHasData;
            visibility[`${month}_standard`] = standardHasData;
        });

        return visibility;
    }, []);

    // Update chart data when memoized data changes
    useEffect(() => {
        setChartData(memoizedChartData);
    }, [memoizedChartData]);

    // Hide empty column groups when data is fully loaded
    useEffect(() => {
        if (!isAmuService) return;
        if (agGridApi && pivotData.length > 0 && isDataFullyLoaded) {
            const visibility = checkColumnVisibility(pivotData, availableMonths);

            // Hide/show columns based on data availability
            Object.entries(visibility).forEach(([groupKey, isVisible]) => {
                const columnsToToggle: string[] = [];

                // Find all column IDs that start with this group key
                availableMonths.forEach((month) => {
                    if (groupKey === `${month}_actm`) {
                        columnsToToggle.push(`${month}_actmVendorPlays`, `${month}_actmTotalPlays`, `${month}_actmMss`);
                    } else if (groupKey === `${month}_standard`) {
                        columnsToToggle.push(
                            `${month}_standardVendorPlays`,
                            `${month}_standardTotalPlays`,
                            `${month}_standardMss`
                        );
                    }
                });

                // Set column visibility using api
                columnsToToggle.forEach((colId) => {
                    agGridApi.setColumnVisible(colId, isVisible);
                });
            });
        }
    }, [agGridApi, pivotData, isDataFullyLoaded, availableMonths, checkColumnVisibility]);

    // Generate column definitions based on available months, toggle state, and service ID
    const columnDefs = useMemo(() => {
        return availableMonths.length > 0
            ? createMssPivotColDefs(availableMonths, isAntiDilutionMode, serviceId)
            : mssPivotColDefsEmpty;
    }, [availableMonths, isAntiDilutionMode]);

    const autoGroupColumnDef = useMemo(() => {
        return {
            field: 'territory',
            minWidth: 250,
            pinned: true
        };
    }, []);

    // Generate default sort model for MSS pivot view (sort by first month's ACTM MSS% column descending)
    const defaultSortModel = useMemo(() => {
        if (availableMonths.length > 0) {
            // Sort by the first (most recent) month's Standard MSS% column in descending order
            const firstMonth = availableMonths[0];
            return [{ colId: `${firstMonth}_${isAmuService ? 'standardMss' : 'mss'}`, sort: 'desc' as const }];
        }
        return [];
    }, [availableMonths]);

    // Handle grid ready event
    const handleGridReady = (api: GridApi) => {
        setAgGridApi(api);
    };

    // Memoize filter change handler to prevent unnecessary re-renders
    const handleFilterChanged = useCallback(() => {
        if (!agGridApi) return;

        // Get current filter model
        const filterModel = agGridApi.getFilterModel();

        // Extract filtered territories
        const territoryFilter = filterModel['territory'];
        const territories = territoryFilter && territoryFilter.values ? territoryFilter.values : [];
        setFilteredTerritories(territories);

        // Extract filtered vendors
        const vendorFilter = filterModel['vendor'];
        const vendors = vendorFilter && vendorFilter.values ? vendorFilter.values : [];
        setFilteredVendors(vendors);
    }, [agGridApi]);

    // Show no data state
    if (isDataFullyLoaded && filteredData.length === 0) {
        return (
            <Column heights="fill" alignmentHorizontal="center" alignmentVertical="center">
                <Text>No MSS data available</Text>
            </Column>
        );
    }

    // Show no data for filters state
    if (chartData.length === 0 && (filteredTerritories.length > 0 || filteredVendors.length > 0)) {
        return (
            <Column heights={['fit', 'fit', 'fill']}>
                {!isDataFullyLoaded && (
                    <Row width="100%" alignmentHorizontal="center" spacing="200">
                        <Text type="b100" color="secondary">
                            Loading data... ({completionPercentage}%)
                        </Text>
                    </Row>
                )}
                <Row width="100%" widths="fill" alignmentVertical="top">
                    <Column heights="fill" alignmentHorizontal="center" alignmentVertical="center">
                        <Text>No data available for current filters</Text>
                    </Column>
                </Row>
                <MetricsGrid
                    columnDefs={columnDefs}
                    rowData={pivotData}
                    filterText={filterText}
                    onGridReady={handleGridReady}
                    onFilterChanged={handleFilterChanged}
                    defaultSortModel={defaultSortModel}
                    autoGroupColumnDef={autoGroupColumnDef}
                    isDataFullyLoaded={isDataFullyLoaded}
                />
            </Column>
        );
    }

    return (
        <Column heights={['fit', 'fill', 'fill']}>
            {!isDataFullyLoaded && (
                <Row width="100%" alignmentHorizontal="center" spacing="200">
                    <Text type="b100" color="secondary">
                        Loading data... ({completionPercentage}%)
                    </Text>
                </Row>
            )}
            <Row width="100%" widths="fill" alignmentVertical="top">
                {chartData.map((chart, index) => {
                    const loadingMonths = getLoadingMonths();

                    if (chart.type === 'line') {
                        return (
                            <MetricsLineChart
                                key={index}
                                title={chart.title}
                                subtitle={chart.subtitle}
                                data={chart.data}
                                xKey={chart.xKey || ''}
                                yKeys={chart.yKeys || []}
                                metricsType="mss"
                                isDataPartial={!isDataFullyLoaded}
                                completionPercentage={completionPercentage}
                                loadingMonths={loadingMonths}
                                loading={loading}
                            />
                        );
                    }
                    return null;
                })}
            </Row>

            <MetricsGrid
                columnDefs={columnDefs}
                rowData={pivotData}
                filterText={filterText}
                onGridReady={handleGridReady}
                onFilterChanged={handleFilterChanged}
                defaultSortModel={defaultSortModel}
                autoGroupColumnDef={autoGroupColumnDef}
                isDataFullyLoaded={isDataFullyLoaded}
            />

            {isAmuService && (
                <Box type="fill" spacingInset="400">
                    <Row spacing="400">
                        <Row alignmentVertical="center">
                            <Text type="b200">Notes: </Text>
                            <Row spacing="none">
                                <Text type="b200">Anti Dilution</Text>
                                <Button
                                    type="link"
                                    size="small"
                                    ref={antiDilutionButtonRef}
                                    onMouseEnter={() => setAntiDilutionMenuOpen(true)}
                                    data-testid="anti-dilution-menu-btn"
                                >
                                    <Icon tokens={infoKnockoutTokens} />
                                </Button>
                                <Popover
                                    anchorNode={antiDilutionButtonRef.current}
                                    open={antiDilutionMenuOpen}
                                    position={'top'}
                                    onClose={() => setAntiDilutionMenuOpen(false)}
                                >
                                    <PopoverHeader closeLabel="Close">{''}</PopoverHeader>
                                    <Column>
                                        <Text>
                                            Table above shows UMG metrics with Anti Dilution/Music-only by default.
                                        </Text>
                                        <Text>
                                            Visit{' '}
                                            <Link
                                                to={'amu-metrics?tab=umg-mss'}
                                                onClick={() => setAntiDilutionMenuOpen(false)}
                                            >
                                                UMG MSS tab
                                            </Link>{' '}
                                            to view both Music-only and Combined plays data in one place.
                                        </Text>
                                    </Column>
                                </Popover>
                            </Row>
                            <Row alignmentVertical="center" spacing="none">
                                <img src={GreenDot} alt="green highlighting" />
                                <Text type="b200">&nbsp;Actuals</Text>
                            </Row>
                            <Row alignmentVertical="center" spacing="none">
                                <Text type="b200">ACTM: Artist Centric Threshold Model</Text>
                            </Row>
                        </Row>
                    </Row>
                </Box>
            )}
        </Column>
    );
};
